// 격자 내 단일 셀 좌표 (col: 열 0~3, row: 행 0~6)
export interface TileCell {
  col: number;
  row: number;
}

// 한 게임에서 사용할 타일 배치 (선택된 셀 목록)
export type TileLayout = TileCell[];

// 가상 Grid 구조 (사용자에게는 노출되지 않음)
export const GRID_COLS = 4;
export const GRID_ROWS = 7;
export const TOTAL_CELLS = GRID_COLS * GRID_ROWS; // 28개 후보 셀

// 실제로 노출하는 타일 개수 (항상 동일)
export const TOTAL_TILES = 8;

// 타일 간 최소 간격 (체비셰프 거리 기준, 인접(상하좌우/대각) 금지)
export const MIN_GAP = 2;

// 본 수행에서 다루는 기억 개수 단계 (4 → 5개) — 총 2 스테이지만 구현
// 게임은 1단계(4개 기억하기)부터 시작하고, 2단계(5개 기억하기)에서 종료된다.
export const SPAN_LEVELS = [4, 5];

// 마지막 스테이지의 기억 개수 (이 span에 도달한 스테이지가 끝나면 게임 종료)
export const MAX_SPAN = SPAN_LEVELS[SPAN_LEVELS.length - 1];

// 점수 테이블 (추후 조정 가능하도록 별도 관리)
// key: 최대 성공 Span, value: { firstTry: 최초 성공 점수, retry: 재수행 후 성공 점수 }
export const SPAN_SCORE_TABLE: Record<number, { firstTry: number; retry: number }> = {
  0: { firstTry: 20, retry: 20 },
  3: { firstTry: 47, retry: 41 },
  4: { firstTry: 63, retry: 54 },
  5: { firstTry: 84, retry: 75 },
  6: { firstTry: 97, retry: 91 },
};

export function getScore(maxSpan: number, isFirstTry: boolean): number {
  const entry = SPAN_SCORE_TABLE[maxSpan] ?? SPAN_SCORE_TABLE[0];
  return isFirstTry ? entry.firstTry : entry.retry;
}

// Span(4~6)을 단계(1~3)로 변환 (4개 기억하기 = 1단계부터 시작)
export function spanToStage(span: number): number {
  return span - 3;
}

// ---------- 격자 배열 생성 ----------

function randomInt(maxExclusive: number): number {
  return Math.floor(Math.random() * maxExclusive);
}

function shuffleArray<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = randomInt(i + 1);
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function chebyshevDistance(a: TileCell, b: TileCell): number {
  return Math.max(Math.abs(a.col - b.col), Math.abs(a.row - b.row));
}

// 좌/우/상/하 분포 및 행·열 과밀 여부 검증
function isWellDistributed(cells: TileCell[]): boolean {
  const leftCount = cells.filter((c) => c.col <= 1).length;
  const rightCount = cells.filter((c) => c.col >= 2).length;
  if (leftCount < 3 || rightCount < 3) return false;

  const topCount = cells.filter((c) => c.row <= 2).length;
  const midCount = cells.filter((c) => c.row >= 3 && c.row <= 4).length;
  const bottomCount = cells.filter((c) => c.row >= 5).length;
  if (topCount < 2 || bottomCount < 2) return false;
  if (midCount < 1) return false;

  // 특정 열에 3개 이상 몰리면 수직 일직선이 되므로 제한
  for (let col = 0; col < GRID_COLS; col++) {
    if (cells.filter((c) => c.col === col).length > 2) return false;
  }
  // 특정 행에 3개 이상 몰리면 수평 일직선이 되므로 제한
  for (let row = 0; row < GRID_ROWS; row++) {
    if (cells.filter((c) => c.row === row).length > 2) return false;
  }

  return true;
}

// 지나치게 규칙적인 형태(2x2 사각형 등) 검사
function isTooRegular(cells: TileCell[]): boolean {
  const cellSet = new Set(cells.map((c) => `${c.col},${c.row}`));
  for (let col = 0; col < GRID_COLS - 1; col++) {
    for (let row = 0; row < GRID_ROWS - 1; row++) {
      if (
        cellSet.has(`${col},${row}`) &&
        cellSet.has(`${col + 1},${row}`) &&
        cellSet.has(`${col},${row + 1}`) &&
        cellSet.has(`${col + 1},${row + 1}`)
      ) {
        return true;
      }
    }
  }
  return false;
}

// 검증 실패 시 최후 수단으로 쓰는 안정적인 기본 배열
function fallbackLayout(): TileLayout {
  return [
    { col: 0, row: 0 },
    { col: 2, row: 0 },
    { col: 0, row: 2 },
    { col: 2, row: 2 },
    { col: 0, row: 4 },
    { col: 2, row: 4 },
    { col: 1, row: 6 },
    { col: 3, row: 6 },
  ];
}

// 게임 시작 시 전체 셀 중 일부를 선택해 타일 배치 생성
export function generateLayout(): TileLayout {
  const allCells: TileCell[] = [];
  for (let row = 0; row < GRID_ROWS; row++) {
    for (let col = 0; col < GRID_COLS; col++) {
      allCells.push({ col, row });
    }
  }

  for (let iter = 0; iter < 2000; iter++) {
    const order = shuffleArray(allCells);
    const cells: TileCell[] = [];
    for (const cell of order) {
      if (cells.length >= TOTAL_TILES) break;
      if (cells.every((c) => chebyshevDistance(c, cell) >= MIN_GAP)) {
        cells.push(cell);
      }
    }

    if (
      cells.length === TOTAL_TILES &&
      isWellDistributed(cells) &&
      !isTooRegular(cells)
    ) {
      return cells;
    }
  }

  return fallbackLayout();
}